import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Mic, Square, Send, Trash2, ChevronDown, ChevronRight,
  Loader2, Wand2, Plus, X,
} from "lucide-react";

const SENTIMENT_STYLE = {
  positiv: { label: "Positiv", cls: "bg-green-500/15 text-green-500 border-green-500/30" },
  neutral: { label: "Neutral", cls: "bg-slate-500/15 text-slate-400 border-slate-500/30" },
  negativ: { label: "Negativ", cls: "bg-red-500/15 text-red-500 border-red-500/30" },
};

const hexToRgba = (hex, a = 0.15) => {
  if (!hex || !hex.startsWith("#")) return `rgba(99,102,241,${a})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("da-DK", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

export default function Evaluering({ user }) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const eventId = localStorage.getItem("selectedEventId");
  const eventName = localStorage.getItem("selectedEventName");
  const isAdmin = ["admin", "admin_assistant", "arrangør"].includes(user?.role);

  const [topics, setTopics] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [inputMethod, setInputMethod] = useState("text"); // 'text' | 'voice'
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [summaries, setSummaries] = useState({}); // topic_id -> { loading, text }
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [newTopicName, setNewTopicName] = useState("");

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const loadData = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const [tRes, eRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/evaluation-topics`, { headers }),
        axios.get(`${API}/events/${eventId}/evaluations`, { headers }),
      ]);
      setTopics(tRes.data || []);
      setEvaluations(eRes.data || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Du har ikke adgang til evalueringer");
        navigate("/");
      } else {
        toast.error("Kunne ikke hente evalueringer");
      }
    }
    setLoading(false);
  };

  const submitEvaluation = async () => {
    const t = text.trim();
    if (!t) { toast.error("Skriv eller indtal en evaluering"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/evaluations`, { text: t, input_method: inputMethod }, { headers });
      toast.success(`Tilføjet under "${res.data.topic_name}"`);
      setText("");
      setInputMethod("text");
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme evaluering");
    }
    setSubmitting(false);
  };

  // ---- Stemmeoptagelse → transskribering ----
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = handleRecordingStop;
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Kunne ikke få adgang til mikrofonen");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const handleRecordingStop = async () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (blob.size === 0) return;
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "optagelse.webm");
      const res = await axios.post(`${API}/events/${eventId}/evaluations/transcribe`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      const transcribed = (res.data.text || "").trim();
      if (transcribed) {
        setText((prev) => (prev ? prev + " " : "") + transcribed);
        setInputMethod("voice");
        toast.success("Tale omsat til tekst — tjek og send");
      } else {
        toast.info("Ingen tale registreret");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke transskribere");
    }
    setTranscribing(false);
  };

  const deleteEvaluation = async (id) => {
    if (!window.confirm("Slet denne evaluering?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}/evaluations/${id}`, { headers });
      setEvaluations((prev) => prev.filter((e) => e.id !== id));
      toast.success("Slettet");
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette");
    }
  };

  const generateSummary = async (topicId) => {
    setSummaries((prev) => ({ ...prev, [topicId]: { loading: true, text: "" } }));
    try {
      const res = await axios.get(`${API}/events/${eventId}/evaluations/summary`, { headers, params: { topic_id: topicId } });
      setSummaries((prev) => ({ ...prev, [topicId]: { loading: false, text: res.data.summary } }));
    } catch (e) {
      setSummaries((prev) => ({ ...prev, [topicId]: { loading: false, text: "" } }));
      toast.error(e?.response?.data?.detail || "Kunne ikke generere opsummering");
    }
  };

  const addTopic = async () => {
    const name = newTopicName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/events/${eventId}/evaluation-topics`, { name }, { headers });
      toast.success("Emne oprettet");
      setNewTopicName("");
      setShowAddTopic(false);
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke oprette emne");
    }
  };

  const deleteTopic = async (topicId) => {
    if (!window.confirm("Slet dette emne?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}/evaluation-topics/${topicId}`, { headers });
      toast.success("Emne slettet");
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette emne");
    }
  };

  const evalsForTopic = (topicId) => evaluations.filter((e) => e.topic_id === topicId);
  const topicsWithData = topics.filter((t) => (t.count || 0) > 0);
  const emptyTopics = topics.filter((t) => (t.count || 0) === 0);

  return (
    <div className="min-h-screen bg-background" data-testid="evaluering-page">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate("/")} variant="ghost" size="sm" data-testid="evaluering-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold uppercase tracking-tight flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" /> Evaluering
              </h1>
              <p className="text-xs text-muted-foreground">{eventName || "AI-baseret evaluering"}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-3xl space-y-6">
        {/* Ny evaluering */}
        <div className="bg-card border border-border rounded-xl p-4 sm:p-5">
          <h2 className="font-semibold mb-1">Ny evaluering</h2>
          <p className="text-xs text-muted-foreground mb-3">
            Skriv det frit — eller tryk på mikrofonen og tal. AI'en sorterer det automatisk i et emne.
          </p>
          <Textarea
            value={text}
            onChange={(e) => { setText(e.target.value); if (inputMethod !== "voice") setInputMethod("text"); }}
            placeholder="F.eks. 'Der manglede trækvogne i Meadow, og leveringen kom for sent...'"
            rows={4}
            className="resize-none"
            data-testid="evaluering-text-input"
          />
          <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
            <div className="flex items-center gap-2">
              {!recording ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={startRecording}
                  disabled={transcribing || submitting}
                  data-testid="evaluering-record-btn"
                >
                  {transcribing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mic className="h-4 w-4 mr-1" />}
                  {transcribing ? "Omsætter..." : "Indtal"}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={stopRecording}
                  className="animate-pulse"
                  data-testid="evaluering-stop-btn"
                >
                  <Square className="h-4 w-4 mr-1" /> Stop optagelse
                </Button>
              )}
              {recording && <span className="text-xs text-red-500">● Optager...</span>}
            </div>
            <Button onClick={submitEvaluation} disabled={submitting || !text.trim()} data-testid="evaluering-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              {submitting ? "Sorterer..." : "Send evaluering"}
            </Button>
          </div>
        </div>

        {/* Oversigt pr. emne */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Emner</h2>
            {isAdmin && (
              <Button variant="ghost" size="sm" onClick={() => setShowAddTopic(true)} data-testid="evaluering-add-topic-btn">
                <Plus className="h-4 w-4 mr-1" /> Nyt emne
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            </div>
          ) : evaluations.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground border border-dashed border-border rounded-xl">
              <Sparkles className="h-6 w-6 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Ingen evalueringer endnu. Skriv eller indtal den første ovenfor.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {topicsWithData.map((topic) => {
                const items = evalsForTopic(topic.id);
                const open = expandedTopic === topic.id;
                const summary = summaries[topic.id];
                return (
                  <div
                    key={topic.id}
                    className="border border-border rounded-xl overflow-hidden"
                    style={{ borderLeftWidth: 4, borderLeftColor: topic.color }}
                    data-testid={`evaluering-topic-${topic.id}`}
                  >
                    <button
                      onClick={() => setExpandedTopic(open ? null : topic.id)}
                      className="w-full flex items-center justify-between gap-2 p-3 hover:bg-accent/5 text-left"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                        <span className="font-medium truncate">{topic.name}</span>
                        {topic.created_by === "ai" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30 shrink-0">AI</span>
                        )}
                      </div>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full shrink-0"
                        style={{ backgroundColor: hexToRgba(topic.color, 0.2), color: topic.color }}
                      >
                        {items.length}
                      </span>
                    </button>

                    {open && (
                      <div className="px-3 pb-3 space-y-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generateSummary(topic.id)}
                          disabled={summary?.loading}
                          data-testid={`evaluering-summary-btn-${topic.id}`}
                        >
                          {summary?.loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1" />}
                          AI-opsummering
                        </Button>
                        {summary?.text && (
                          <div className="text-sm bg-primary/5 border border-primary/20 rounded-lg p-3 whitespace-pre-wrap" data-testid={`evaluering-summary-${topic.id}`}>
                            {summary.text}
                          </div>
                        )}
                        {items.map((ev) => {
                          const sent = SENTIMENT_STYLE[ev.sentiment] || SENTIMENT_STYLE.neutral;
                          const canDelete = isAdmin || ev.author_email === user?.email;
                          return (
                            <div key={ev.id} className="bg-muted/20 rounded-lg p-3" data-testid={`evaluering-item-${ev.id}`}>
                              <p className="text-sm whitespace-pre-wrap break-words">{ev.text}</p>
                              <div className="flex items-center justify-between gap-2 mt-2">
                                <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
                                  <span className={`px-1.5 py-0.5 rounded border ${sent.cls}`}>{sent.label}</span>
                                  <span>{ev.author_name}</span>
                                  <span>· {fmtTime(ev.created_at)}</span>
                                  {ev.input_method === "voice" && <Mic className="h-3 w-3" />}
                                </div>
                                {canDelete && (
                                  <button
                                    onClick={() => deleteEvaluation(ev.id)}
                                    className="text-muted-foreground hover:text-red-500 transition-colors"
                                    title="Slet"
                                    data-testid={`evaluering-delete-${ev.id}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Tomme emner (kompakt) */}
              {emptyTopics.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs text-muted-foreground mb-2">Emner uden evalueringer endnu</p>
                  <div className="flex flex-wrap gap-2">
                    {emptyTopics.map((t) => (
                      <span
                        key={t.id}
                        className="group inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border"
                        style={{ backgroundColor: hexToRgba(t.color, 0.12), color: t.color, borderColor: hexToRgba(t.color, 0.4) }}
                      >
                        {t.name}
                        {isAdmin && !t.is_default && (
                          <button onClick={() => deleteTopic(t.id)} className="opacity-60 hover:opacity-100" title="Slet emne">
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Tilføj emne modal */}
      {showAddTopic && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowAddTopic(false)}>
          <div className="bg-card border border-border rounded-t-xl sm:rounded-lg p-6 w-full sm:max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Nyt emne</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddTopic(false)}><X className="h-4 w-4" /></Button>
            </div>
            <Textarea
              value={newTopicName}
              onChange={(e) => setNewTopicName(e.target.value.replace(/\n/g, ""))}
              placeholder="Emnenavn (1-3 ord)"
              rows={1}
              className="resize-none mb-3"
              data-testid="evaluering-new-topic-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddTopic(false)}>Annuller</Button>
              <Button onClick={addTopic} data-testid="evaluering-save-topic-btn">Opret</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
